import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from '../../environments/environment';

// Define type for chat history entries used within the service
type ChatMessage = {
    role: 'user' | 'assistant';
    content: string;
    expanded?: boolean;
};

// Interface for the functions the service needs from the component
export interface AIAgentCallbacks {
    checkImageFn: (sheetId: number) => Promise<string | null>;
    submitFn: (sheetId: number, prompt: string) => Promise<void>; // Modified to accept prompt
    updateHistoryFn: (sheetId: number, message: ChatMessage) => void;
    triggerChangeDetectionFn: () => void;
    setLoadingTextFn: (sheetId: number, text: string) => void;
    getAgentModeFn: (sheetId: number) => boolean; // Check agent mode specifically for the sheet
    setAgentModeFn: (sheetId: number, mode: boolean) => void; // Set agent mode specifically for the sheet
    recreateModelFn: (sheetId: number) => Promise<void>; // Add function to trigger model recreation
}


@Injectable({
    providedIn: 'root' // Provide service at the root level
})
export class AIAgentService {
    private agentCycles: { [sheetId: number]: number } = {}; // Track cycles per sheet
    private agentAborted: { [sheetId: number]: boolean } = {}; // Track abortion per sheet
    private abortControllerMap: { [sheetId: number]: AbortController } = {}; // Store AbortControllers per sheet if needed for fine-grained control within the service run

    private readonly MAX_AGENT_CYCLES = 3;
    private functionsBaseUrl = 'https://us-central1-xbuild3d.cloudfunctions.net'; // Adjust if your region/project ID is different
    private apiUrl: string;

    constructor(private toastr: ToastrService, private http: HttpClient) {
        this.apiUrl = this.functionsBaseUrl;
    } // Inject ToastrService and HttpClient

    public isRunning(sheetId: number): boolean {
        return !!this.agentCycles[sheetId] && this.agentCycles[sheetId] > 0 && !this.agentAborted[sheetId];
    }

    // Method to initiate the agent cycle
    public async startAgentCycle(sheetId: number, initialResponse: string, callbacks: AIAgentCallbacks): Promise<void> {
        console.log(`Agent cycle initiated for sheet ${sheetId}`);
        this.agentCycles[sheetId] = 0; // Reset/Initialize cycle count
        this.agentAborted[sheetId] = false;
        // Create and store an AbortController for this run
        this.abortControllerMap[sheetId] = new AbortController();


        await this.runAgentStep(sheetId, initialResponse, callbacks);
    }

    // Method to explicitly stop the agent cycle for a sheet
    public stopAgentCycle(sheetId: number, callbacks: AIAgentCallbacks): void {
        if (this.isRunning(sheetId)) {
            console.log(`Stopping agent cycle explicitly for sheet ${sheetId}`);
            this.agentAborted[sheetId] = true;
            this.abortControllerMap[sheetId]?.abort(); // Abort ongoing operations controlled by this service run
            // Clean up state
            delete this.agentCycles[sheetId];
            delete this.abortControllerMap[sheetId];

            callbacks.setLoadingTextFn(sheetId, '');
            callbacks.triggerChangeDetectionFn();
            this.toastr.info(`Agent cycle stopped for sheet ${sheetId}.`, 'Agent Stopped');
        }
    }


    // Recursive step execution
    private async runAgentStep(sheetId: number, previousResponse: string, callbacks: AIAgentCallbacks): Promise<void> {
        // Check if aborted before starting the step
        if (this.agentAborted[sheetId] || !callbacks.getAgentModeFn(sheetId)) {
            console.log(`Agent step skipped for sheet ${sheetId}: Aborted or agent mode disabled.`);
            this.cleanupAgentState(sheetId); // Clean up if aborted externally
            return;
        }

        const currentCycle = this.agentCycles[sheetId];
        console.log(`Agent cycle ${currentCycle + 1} started for sheet ${sheetId}`);

        // Use the abort signal associated with this specific run
        const signal = this.abortControllerMap[sheetId]?.signal;

        try {
            // --- Trigger model recreation BEFORE the image check ---
            console.log(`Agent cycle for sheet ${sheetId}: Ensuring model is up-to-date before check.`);
            await callbacks.recreateModelFn(sheetId);
            // --- End model recreation trigger ---

            // 1. Run Image Check
            console.log(`Agent cycle for sheet ${sheetId}: Running image check.`);
            callbacks.setLoadingTextFn(sheetId, `Agent: Checking view (Cycle ${currentCycle + 1})...`);
            callbacks.triggerChangeDetectionFn();
            const checkResult = await callbacks.checkImageFn(sheetId); // Assumes checkImageFn respects its own abort signal if passed

            // Check if aborted during the check OR agent mode was turned off OR check failed
            if (this.agentAborted[sheetId] || signal?.aborted || !callbacks.getAgentModeFn(sheetId) || checkResult === null) {
                console.log(`Agent cycle for sheet ${sheetId} aborted or agentMode disabled during check, or check failed.`);
                this.cleanupAgentState(sheetId);
                callbacks.setLoadingTextFn(sheetId, '');
                callbacks.triggerChangeDetectionFn();
                return;
            }

            // 2. Add Check Result to History
            callbacks.updateHistoryFn(sheetId, { role: 'assistant', content: `**Agent Check Result (Cycle ${currentCycle + 1}):**\n${checkResult}`, expanded: false });
            callbacks.triggerChangeDetectionFn(); // Ensure UI updates

            // 3. Basic stopping condition based on check result
            const stopKeywords = ['looks good', 'accurate', 'correct', 'matches', 'no discrepancies'];
            if (stopKeywords.some(keyword => checkResult.toLowerCase().includes(keyword))) {
                console.log(`Agent cycle for sheet ${sheetId} stopping: Check result suggests satisfaction.`);
                this.toastr.success('Agent check indicates model is satisfactory.', 'Agent Complete');
                callbacks.setAgentModeFn(sheetId, false); // Turn off agent mode for this sheet
                this.cleanupAgentState(sheetId);
                callbacks.setLoadingTextFn(sheetId, '');
                callbacks.triggerChangeDetectionFn();
                return;
            }

            // 4. Check cycle limit
            if (currentCycle + 1 >= this.MAX_AGENT_CYCLES) {
                console.warn(`Agent cycle limit (${this.MAX_AGENT_CYCLES}) reached for sheet ${sheetId}. Stopping agent.`);
                callbacks.updateHistoryFn(sheetId, { role: 'assistant', content: `**Agent:** Maximum refinement cycles reached. Please review or provide a new prompt.`, expanded: true });
                this.toastr.info('Agent mode reached maximum cycles and has been disabled.', 'Agent Stopped');
                callbacks.setAgentModeFn(sheetId, false); // Turn off agent mode
                this.cleanupAgentState(sheetId);
                callbacks.setLoadingTextFn(sheetId, '');
                callbacks.triggerChangeDetectionFn();
                return;
            }


            // 5. Prepare next prompt
            const nextPrompt = `Based on the previous result and the image analysis ("${checkResult}"), please refine the spreadsheet data to address any discrepancies or improve accuracy. Focus on the issues raised in the analysis.`;
            // Note: The component's onAISubmit will handle adding this prompt to history as 'user'

            // 6. Resubmit
            this.agentCycles[sheetId]++; // Increment cycle count for the *next* step
            console.log(`Agent cycle for sheet ${sheetId}: Resubmitting with refined prompt for cycle ${this.agentCycles[sheetId] + 1}.`);
            callbacks.setLoadingTextFn(sheetId, `Agent: Refining (Cycle ${this.agentCycles[sheetId] + 1})...`);
            callbacks.triggerChangeDetectionFn();


            // Call the submit function provided by the component
            // Pass the signal for potential cancellation within the submitFn if it supports it
            await callbacks.submitFn(sheetId, nextPrompt); // submitFn now needs to handle the agent logic recursively or trigger the next step

            // --- Important: How the loop continues depends on submitFn ---
            // Option A: submitFn calls runAgentStep again internally (requires passing callbacks etc.) - Complex
            // Option B: Component's onAISubmit, after getting a response, checks agent mode *again* and calls startAgentCycle or runAgentStep - Simpler flow
            // Assuming Option B: The component's onAISubmit will receive the response and if agent mode is still on, it will call back into this service (likely startAgentCycle again, which calls runAgentStep). The cycle count is managed here.


        } catch (error: any) {
            // Check if error is due to abortion
            if (error.name === 'AbortError' || signal?.aborted || this.agentAborted[sheetId]) {
                console.log(`Agent cycle for sheet ${sheetId} aborted during operation.`);
                this.toastr.info('Agent cycle aborted.', 'Aborted');
            } else {
                console.error(`Error during agent cycle ${currentCycle + 1} for sheet ${sheetId}:`, error);
                this.toastr.error('An error occurred during the agent refinement cycle.', 'Agent Error');
                callbacks.updateHistoryFn(sheetId, { role: 'assistant', content: `**Agent Error (Cycle ${currentCycle + 1}):** Failed to complete refinement cycle. ${error.message || 'Unknown error'}`, expanded: true });
            }
            callbacks.setAgentModeFn(sheetId, false); // Turn off agent mode on error or abort
            this.cleanupAgentState(sheetId);
            callbacks.setLoadingTextFn(sheetId, '');
            callbacks.triggerChangeDetectionFn();
        }
        // Removed finally block, cleanup is handled on exit paths
    }

    private cleanupAgentState(sheetId: number): void {
        console.log(`Cleaning up agent state for sheet ${sheetId}`);
        delete this.agentCycles[sheetId];
        delete this.agentAborted[sheetId];
        delete this.abortControllerMap[sheetId];
    }

    generateSubPrompts(userInput: string, aiProvider: string): Observable<any> {
        const url = `${this.functionsBaseUrl}/generateSubPrompts`;
        const body = {
            userInput: userInput,
            aiProvider: aiProvider // Pass the selected provider
        };
        console.log('Calling generateSubPrompts with:', body);
        return this.http.post<any>(url, body).pipe(
            tap(response => console.log('Received sub-projects response:', response)),
            catchError(error => {
                console.error('Error generating sub-projects:', error);
                // Optionally transform the error or re-throw
                return throwError(() => new Error('Failed to generate sub-projects: ' + error.message));
            })
        );
    }

    generateGeminiStream(payload: any): Observable<string> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        // The backend URL for the streaming endpoint
        const streamUrl = `${this.apiUrl}/createGeminiPromptStream`;

        return new Observable<string>(observer => {
            const fetchStream = async () => {
                try {
                    const response = await fetch(streamUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            // Add any other necessary headers, like Authorization if your endpoint is protected
                        },
                        body: JSON.stringify(payload),
                    });

                    if (!response.ok) {
                        // Handle HTTP errors (e.g., 4xx, 5xx)
                        const errorText = await response.text();
                        observer.error(new Error(`HTTP error! status: ${response.status}, ${errorText}`));
                        return;
                    }

                    if (!response.body) {
                        observer.error(new Error('Response body is null'));
                        return;
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder('utf-8');

                    const processText = async () => {
                        while (true) {
                            const { done, value } = await reader.read();
                            if (done) {
                                observer.complete();
                                break;
                            }
                            const chunk = decoder.decode(value, { stream: true });
                            observer.next(chunk);
                        }
                    };

                    processText().catch(err => observer.error(err));

                } catch (error) {
                    observer.error(error);
                }
            };

            fetchStream();

            // Cleanup function for when the Observable is unsubscribed
            return () => {
                // You might need to add logic here to cancel the fetch request if possible/necessary,
                // for example, using an AbortController.
                console.log('Unsubscribed from Gemini stream');
            };
        });
    }

    // Stub methods for missing AI provider calls
    generateOpenAIText(payload: any): Observable<any> {
        console.warn('AIAgentService.generateOpenAIText is a stub and not fully implemented.', payload);
        // Replace with actual API call to your OpenAI endpoint
        // Example: return this.http.post(`${this.apiUrl}/createOpenAIPrompt`, payload);
        return throwError(() => new Error('OpenAI text generation not implemented in service.'));
    }

    generateClaudeText(payload: any): Observable<any> {
        console.warn('AIAgentService.generateClaudeText is a stub and not fully implemented.', payload);
        // Example: return this.http.post(`${this.apiUrl}/createClaudePrompt`, payload);
        return throwError(() => new Error('Claude text generation not implemented in service.'));
    }

    generateGeminiText(payload: any): Observable<any> {
        console.warn('AIAgentService.generateGeminiText is a stub and not fully implemented.', payload);
        // Example: return this.http.post(`${this.apiUrl}/createGeminiPrompt`, payload);
        return throwError(() => new Error('Gemini non-streaming text generation not implemented in service.'));
    }

    generateDeepSeekText(payload: any): Observable<any> {
        console.warn('AIAgentService.generateDeepSeekText is a stub and not fully implemented.', payload);
        // Example: return this.http.post(`${this.apiUrl}/createDeepSeekPrompt`, payload);
        return throwError(() => new Error('DeepSeek text generation not implemented in service.'));
    }

    generateGrokText(payload: any): Observable<any> {
        console.warn('AIAgentService.generateGrokText is a stub and not fully implemented.', payload);
        // Example: return this.http.post(`${this.apiUrl}/createGrokPrompt`, payload);
        return throwError(() => new Error('Grok text generation not implemented in service.'));
    }

    // Add other methods like analyzeModel if needed...
} 